import { Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { get } from "@/lib/api";
import { AdminSubmissions } from "@/components/admin/AdminSubmissions";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { AdminCommunities } from "@/components/admin/AdminCommunities";
import { AdminArticles } from "@/components/admin/AdminArticles";

const Metric = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-[#0B2150]">{value ?? "—"}</p>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const { data: s } = useQuery({ queryKey: ["admin-stats"], queryFn: () => get("/admin/stats"), enabled: !!user });
  if (user === null) return <div className="p-12 text-slate-600">Checking session…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <Seo title="Admin dashboard" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold text-[#0B2150]">Dashboard</h1>
          <p className="text-sm text-slate-600">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border px-4 py-2 text-sm" data-testid="admin-sitemap-link">Sitemap XML <ExternalLink className="h-3.5 w-3.5" /></a>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-[#0B2150] text-white px-4 py-2 text-sm font-semibold hover:bg-[#03A896]" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Metric label="Communities" value={s?.communities?.toLocaleString()} testid="admin-metric-communities" />
        <Metric label="Pending submissions" value={s?.pending_submissions} testid="admin-metric-pending" />
        <Metric label="Unread messages" value={s?.unread_messages} testid="admin-metric-unread" />
        <Metric label="Articles" value={s?.articles} testid="admin-metric-articles" />
        <Metric label="Verified / researched" value={s ? (s.by_status.verified || 0) + (s.by_status.researched || 0) : null} testid="admin-metric-verified" />
      </div>
      <Tabs defaultValue="submissions">
        <TabsList className="bg-white border mb-6 flex-wrap h-auto">
          <TabsTrigger value="submissions" data-testid="admin-tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="communities" data-testid="admin-tab-communities">Communities</TabsTrigger>
          <TabsTrigger value="articles" data-testid="admin-tab-articles">Articles</TabsTrigger>
        </TabsList>
        <TabsContent value="submissions"><AdminSubmissions /></TabsContent>
        <TabsContent value="messages"><AdminMessages /></TabsContent>
        <TabsContent value="communities"><AdminCommunities /></TabsContent>
        <TabsContent value="articles"><AdminArticles /></TabsContent>
      </Tabs>
      <p className="mt-10 text-xs text-slate-500"><Link to="/" className="underline">Back to site</Link></p>
    </div>
  );
}
